import React, {
    useRef,
    useState,
    useEffect,
    HTMLProps,
    useImperativeHandle,
    forwardRef,
    ForwardedRef,
} from 'react';
import merge from 'lodash/merge';
import noop from 'lodash/noop';
import useMountedState from 'react-use/lib/useMountedState';
import styled, { css, CSSProperties } from 'styled-components';
import { shouldIgnoreProp } from '../../utils/shouldIgnoreProp';
import { breakpoint, BreakpointProp } from '../../utils/styles/breakpoints';
import { withLazyLoad } from '../../utils/withLazyLoad';
import { Skeleton } from '../feedback/Skeleton';

interface ImageProps extends HTMLProps<HTMLImageElement> {
    objectFit?: BreakpointProp<CSSProperties['objectFit']>;
    objectPosition?: BreakpointProp<CSSProperties['objectPosition']>;
}

const Img = styled.img.withConfig({
    displayName: 'Img',
    shouldForwardProp: shouldIgnoreProp('objectFit', 'objectPosition', 'isLoaded'),
})<ImageProps & { isLoaded: boolean }>`
    display: block;
    width: 100%;
    object-position: center;
    -webkit-user-drag: none;
    user-drag: none;
    user-select: none;
    transition: opacity 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
    pointer-events: none;

    ${css<ImageProps & { isLoaded: boolean }>((props) =>
        merge(
            breakpoint('opacity', props.isLoaded ? 1 : 0),
            breakpoint('objectFit', props.objectFit ?? 'cover'),
            breakpoint('objectPosition', props.objectPosition),
        ),
    )}
`;

export const Image = withLazyLoad(
    forwardRef((props: ImageProps, ref: ForwardedRef<HTMLImageElement | null>) => {
        const isMounted = useMountedState();
        const [isLoaded, setIsLoaded] = useState(false);
        const innerRef = useRef<HTMLImageElement | null>(null);

        useImperativeHandle<HTMLImageElement | null, HTMLImageElement | null>(
            ref,
            () => innerRef.current,
        );

        useEffect(() => {
            if (!innerRef.current) {
                return noop;
            }

            if (!innerRef.current.complete) {
                const onImageLoaded = () => {
                    if (isMounted()) {
                        setIsLoaded(true);
                    }
                };

                innerRef.current.addEventListener('load', onImageLoaded);
                innerRef.current.addEventListener('error', props.onError ?? noop);

                return () => {
                    innerRef.current?.removeEventListener('load', onImageLoaded);
                    innerRef.current?.removeEventListener('error', props.onError ?? noop);
                };
            }

            setIsLoaded(true);

            return noop;
        }, [innerRef.current]);

        return <Img {...props} ref={innerRef} isLoaded={isLoaded} />;
    }),
    forwardRef((_, ref: ForwardedRef<HTMLDivElement>) => (
        <Skeleton variant="rect" width="100%" height="100%" ref={ref} />
    )),
);

Image.defaultProps = {
    objectFit: 'cover',
};
